"use client";

import { useMemo, useState } from "react";
import { DocumentPreview } from "@/components/tool-kit/document-preview";
import {
  Panel,
  PillGroup,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import { BUTTON_SECONDARY } from "@/components/tool-kit/styles";
import { useToolUi } from "@/components/tools/tool-ui";
import {
  buildCaseBrief,
  type CaseBriefInput,
  type CaseBriefLayout,
} from "@/lib/tool-kit/essay-templates/case-brief";

/** English strings. A translated content file overrides these through `ui`. */
export const CASE_BRIEF_GENERATOR_STRINGS = {
  tryExample: "Try an example",
  clear: "Clear the form",
  layoutLabel: "Layout",
  layouts: {
    standard: "Classic case brief",
    firac: "FIRAC (Facts, Issue, Rule, Application, Conclusion)",
  },
  caseHeading: "The case",
  caseNameLabel: "Case name",
  caseNamePlaceholder: "e.g. Hawkins v. McGee",
  citationLabel: "Citation (reporter, volume, page)",
  citationPlaceholder: "e.g. 84 N.H. 114, 146 A. 641",
  courtLabel: "Court",
  courtPlaceholder: "e.g. Supreme Court of New Hampshire",
  yearLabel: "Year decided",
  yearPlaceholder: "e.g. 1929",
  backgroundHeading: "Facts and procedure",
  factsLabel: "Facts",
  factsPlaceholder: "The legally relevant facts: who did what to whom.",
  historyLabel: "Procedural history",
  historyPlaceholder: "What happened in the lower courts, and who appealed?",
  analysisHeading: "The court's decision",
  issuesLabel: "Issue(s) — one per line, each as a question",
  issuesPlaceholder:
    "e.g. Is a doctor's promise of a “perfect hand” a binding warranty?",
  holdingLabel: "Holding",
  holdingPlaceholder: "The court's answer to each issue.",
  ruleLabel: "Rule of law",
  rulePlaceholder: "The legal principle the court applied or announced.",
  reasoningLabel: "Reasoning",
  reasoningPlaceholder: "How the court got from the rule to the holding.",
  dispositionLabel: "Disposition",
  dispositionPlaceholder: "e.g. Reversed and remanded",
  extrasHeading: "Beyond the majority",
  separateLabel: "Concurring and dissenting opinions",
  separatePlaceholder: "Who wrote separately, and where they disagreed.",
  notesLabel: "Your notes",
  notesPlaceholder: "Why the case matters, and questions to raise in class.",
  fileName: "case-brief",
  document: {
    heading: "Your case brief",
    prompt: "To write",
    guidance: "Tip",
    copy: "Copy",
    copied: "Copied!",
    downloadText: "Download .txt",
    downloadMarkdown: "Download .md",
    print: "Print",
    remaining: "one: part still to write|other: parts still to write",
    complete: "Every part of the brief has your content.",
  },
  defaultTitle: "Case Brief",
  subtitle: {
    standard: "Case brief",
    firac: "Case brief · FIRAC",
  },
  fields: {
    caseName: "Case",
    citation: "Citation",
    court: "Court",
    year: "Decided",
  },
  citationNote:
    "Check the full citation against the format your course requires — usually The Bluebook in U.S. law schools.",
  headings: {
    facts: "Facts",
    history: "Procedural History",
    issues: "Issue",
    holding: "Holding",
    rule: "Rule of Law",
    reasoning: "Reasoning",
    disposition: "Disposition",
    separate: "Concurring and Dissenting Opinions",
    notes: "Notes and Significance",
    firacFacts: "F — Facts",
    firacIssue: "I — Issue",
    firacRule: "R — Rule",
    firacApplication: "A — Application",
    firacConclusion: "C — Conclusion",
  },
  prompts: {
    facts:
      "Summarize the legally relevant facts: who the parties are, what happened between them, and what the plaintiff asked for. Leave out details the court did not rely on.",
    history:
      "Explain how the case reached this court: the trial result, any appeals, and which party is appealing.",
    issues:
      "Frame each issue as a yes-or-no question that joins the legal rule to the key facts: “Is … liable when …?”",
    holding:
      "Answer each issue in one sentence: yes or no, with the essential reason.",
    rule: "State the rule the court applied or announced, in general terms that would decide future cases.",
    reasoning:
      "Trace how the court got from the rule to the holding: the steps of its argument, the policy reasons, and the precedents it followed or distinguished.",
    disposition:
      "Record what the court did with the decision below: affirmed, reversed, remanded, or vacated.",
    separate:
      "Summarize each concurring or dissenting opinion: who wrote it and where it parted from the majority. Write “None” if there were none.",
    notes:
      "Add your own analysis: why the case is in your casebook, how it relates to other cases, and questions to raise in class.",
  },
  issueGuidance:
    "Each issue should be answerable yes or no, and your holding should answer it in the same order.",
  example: {
    caseName: "Palsgraf v. Long Island Railroad Co.",
    citation: "248 N.Y. 339, 162 N.E. 99",
    court: "Court of Appeals of New York",
    year: "1928",
    facts:
      "Helen Palsgraf was standing on a platform of the defendant's railroad after buying a ticket. Two men ran to catch a departing train; one, carrying a small package wrapped in newspaper, jumped aboard but seemed about to fall. A guard on the train pulled him in while a guard on the platform pushed him from behind, and the package fell onto the rails. It contained fireworks, which exploded. The shock knocked over scales at the other end of the platform, many feet away, and they struck and injured Palsgraf. Nothing about the package suggested that it was dangerous.",
    history:
      "Palsgraf sued the railroad for negligence and won a jury verdict at trial. The Appellate Division affirmed, and the railroad appealed to the Court of Appeals.",
    issues:
      "Is the railroad liable in negligence to a plaintiff who was outside the range of foreseeable danger created by its employees' conduct?",
    holding:
      "No. Even if the guards were negligent toward the man with the package, they committed no wrong toward Palsgraf, because no harm to her was reasonably foreseeable.",
    rule: "A defendant owes a duty of care only to those within the zone of reasonably foreseeable risk; negligence toward one person does not create liability to an unforeseeable plaintiff.",
    reasoning:
      "Writing for a 4–3 majority, Chief Judge Cardozo treated negligence as a relation between parties: a plaintiff must show a wrong to herself, not merely a wrong to someone else. Nothing about a newspaper-wrapped package signaled that pushing its carrier could endanger a person standing far down the platform, so the guards breached no duty owed to Palsgraf. In Cardozo's words, “the risk reasonably to be perceived defines the duty to be obeyed.”",
    disposition: "Reversed; the complaint was dismissed.",
    separate:
      "Judge Andrews, joined by two judges, dissented: everyone owes the world at large a duty not to act in ways that unreasonably threaten others' safety, and the real question is proximate cause — a matter of policy and fairness. He would have affirmed the verdict for Palsgraf.",
    notes:
      "The leading case on duty and the unforeseeable plaintiff. Torts courses pair Cardozo's duty-based approach with Andrews's proximate-cause approach to frame the debate. Question for class: would the outcome change if the package had been marked as fireworks?",
  },
} as const;

const EMPTY: CaseBriefInput = {
  layout: "standard",
  caseName: "",
  citation: "",
  court: "",
  year: "",
  facts: "",
  history: "",
  issues: "",
  holding: "",
  rule: "",
  reasoning: "",
  disposition: "",
  separate: "",
  notes: "",
};

const LAYOUTS: readonly CaseBriefLayout[] = ["standard", "firac"];

type TextKey = Exclude<keyof CaseBriefInput, "layout">;

export default function CaseBriefGeneratorWidget() {
  const strings = useToolUi(CASE_BRIEF_GENERATOR_STRINGS);
  const [input, setInput] = useState<CaseBriefInput>(EMPTY);
  const doc = useMemo(() => buildCaseBrief(input, strings), [input, strings]);
  const update = (key: TextKey) => (value: string) =>
    setInput((previous) => ({ ...previous, [key]: value }));
  const area = (key: TextKey, label: string, placeholder: string, rows = 3) => (
    <TextAreaField
      id={`cb-${key}`}
      label={label}
      onChange={update(key)}
      placeholder={placeholder}
      prose
      rows={rows}
      value={input[key]}
    />
  );

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={() =>
            setInput((previous) => ({
              ...EMPTY,
              ...strings.example,
              layout: previous.layout,
            }))
          }
          type="button"
        >
          {strings.tryExample}
        </button>
        <button
          className={BUTTON_SECONDARY}
          onClick={() =>
            setInput((previous) => ({ ...EMPTY, layout: previous.layout }))
          }
          type="button"
        >
          {strings.clear}
        </button>
      </div>

      <PillGroup
        label={strings.layoutLabel}
        name="cb-layout"
        onChange={(layout) => setInput((previous) => ({ ...previous, layout }))}
        options={LAYOUTS.map((value) => ({
          value,
          label: strings.layouts[value],
        }))}
        value={input.layout}
      />

      <Panel title={strings.caseHeading}>
        <TextField
          id="cb-case-name"
          label={strings.caseNameLabel}
          onChange={update("caseName")}
          placeholder={strings.caseNamePlaceholder}
          value={input.caseName}
        />
        <div className="grid gap-4 sm:grid-cols-[1fr_1fr_8rem]">
          <TextField
            id="cb-citation"
            label={strings.citationLabel}
            onChange={update("citation")}
            placeholder={strings.citationPlaceholder}
            value={input.citation}
          />
          <TextField
            id="cb-court"
            label={strings.courtLabel}
            onChange={update("court")}
            placeholder={strings.courtPlaceholder}
            value={input.court}
          />
          <TextField
            id="cb-year"
            label={strings.yearLabel}
            onChange={update("year")}
            placeholder={strings.yearPlaceholder}
            value={input.year}
          />
        </div>
      </Panel>

      <Panel title={strings.backgroundHeading}>
        {area("facts", strings.factsLabel, strings.factsPlaceholder, 5)}
        {area("history", strings.historyLabel, strings.historyPlaceholder)}
      </Panel>

      <Panel title={strings.analysisHeading}>
        {area("issues", strings.issuesLabel, strings.issuesPlaceholder, 2)}
        {area("holding", strings.holdingLabel, strings.holdingPlaceholder)}
        {area("rule", strings.ruleLabel, strings.rulePlaceholder)}
        {area(
          "reasoning",
          strings.reasoningLabel,
          strings.reasoningPlaceholder,
          5
        )}
        <TextField
          id="cb-disposition"
          label={strings.dispositionLabel}
          onChange={update("disposition")}
          placeholder={strings.dispositionPlaceholder}
          value={input.disposition}
        />
      </Panel>

      <Panel title={strings.extrasHeading}>
        {area("separate", strings.separateLabel, strings.separatePlaceholder)}
        {area("notes", strings.notesLabel, strings.notesPlaceholder)}
      </Panel>

      <DocumentPreview
        doc={doc}
        fileName={strings.fileName}
        idPrefix="cb"
        labels={strings.document}
      />
    </div>
  );
}
